import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Paper,
  TableContainer,
  TablePagination
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import { useTiersGameContext } from '../context/TiersGameContext';

const TiersPlayerPicks = ({ interval = 1 }) => {
  const {
    leaderboardData,
    myLeaderboardInfo,
    myUserId,
    isUserInThisPool,
    intervalInfo,
  } = useUserAndGameContext();
  
  const {
    players,
    hasThisIntervalsDeadlinePassed,
  } = useTiersGameContext();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  const intervalName = intervalInfo?.info?.find(info => info.interval === parseInt(interval))?.label;
 
  
  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const formatScore = (score) => {
    if (score === undefined || score === null) {
      return '(N/A)';
    } else if (score === 0) {
      return '(E)';
    } else if (score > 0) {
      return `(+${score})`;
    } else {
      return `(${score})`;
    }
  };

  const findScore = (entry, interval) => {
    if (interval === 'Overall') {
      return formatScore(entry?.overallScore);
    } else {
      // in Interval Data find the object where interval === interval
      const intervalData = entry?.intervalData?.find(
        data => data.interval === interval || data.interval === parseInt(interval)
      );
      return formatScore(intervalData?.intervalScore);
    }
  };

  const findRank = (entry, interval) => {
    if (interval === 'Overall') {
      return entry?.rank;
    } else {
      // in Interval Data find the object where interval === interval
      const intervalData = entry?.intervalData?.find(
        data => data.interval === interval || data.interval === parseInt(interval)
      );
      return intervalData?.rank;
    }
  };

  const getPlayerName = (playerId) => {
    if (!players || !playerId) return "No Pick";
    
    const player = players.find(p => p.PlayerID === Number(playerId));
    return player ? `${player.FirstName} ${player.LastName}` : "No Pick";
  };

  // Find the relevant interval data and players for a specific entry
  const getIntervalPlayersForEntry = (entry, interval) => {
    if (!entry || !entry.intervalData) return [];
    
    const intervalData = entry.intervalData.find(data => 
      data.interval === interval || data.interval === parseInt(interval)
    );
    
    if (!intervalData || !intervalData.players) return [];
    
    // Sort players by tier
    return [...intervalData.players].sort((a, b) => a.tier - b.tier);
  };

  // Determine the two worst tiers for a specific entry
  const getWorstTiersForEntry = (players) => {
    if (!players || players.length === 0) return [];
    
    // Convert to array for sorting with their scores
    const picksArray = players.map(player => ({
      tier: player.tier,
      overallScore: player.overallScore
    }));
    
    // Filter out picks that don't have scores
    const picksWithScores = picksArray.filter(pick => pick.overallScore !== null && pick.overallScore !== undefined);
    
    // Sort by score (highest/worst first)
    // If tied, sort by tier (descending, so higher tiers come first)
    const sortedPicks = picksWithScores.sort((a, b) => {
      if (a.overallScore !== b.overallScore) {
        return b.overallScore - a.overallScore; // Sort by score descending (worst first)
      }
      return b.tier - a.tier; // If tied, sort by tier descending (higher first)
    });
    
    // Take the first two (worst) picks
    const twoWorstPicks = sortedPicks.slice(0, 2);
    
    // Return the tiers of the two worst picks
    return twoWorstPicks.map(pick => pick.tier);
  };

  // Format player score with status
  const formatPlayerScore = (player) => {
    let playerScore = null;
    let statusText = '';
    
    // Determine status
    if (player.cut) {
      statusText = 'Cut';
    } else if (player.withdrawn) {
      statusText = 'WD';
    }
    
    // Determine score
    if (player.overallScore !== undefined && player.overallScore !== null) {
      if (player.overallScore === 0) {
        playerScore = '(E';
      } else if (player.overallScore > 0) {
        playerScore = `(+${player.overallScore}`;
      } else {
        playerScore = `(${player.overallScore}`;
      }
      
      // Add status if exists and close parenthesis
      if (statusText) {
        playerScore += ` ${statusText})`;
      } else {
        playerScore += ')';
      }
    } else if (statusText) {
      // If no score but has status
      playerScore = `(${statusText})`;
    }
    
    return playerScore;
  };

  // Function to render player tiers
  const renderPlayerTiers = (player, worstTiers) => {
    const playerScore = formatPlayerScore(player);
    const isWorstTier = worstTiers.includes(player.tier);
    
    return (
      <Box 
        key={`tier-${player.tier}`}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          marginBottom: '4px',
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: isMobile ? '10px' : '14px',
            color: isWorstTier ? '#CC293C' : '#002129',
            fontWeight: '700',
            textAlign: 'left',
            whiteSpace: 'nowrap',
          }}
        >
          <Box component="span">Tier {player.tier}: </Box>
          <Box 
            component="span" 
            sx={{ 
              display: 'inline-block',
              maxWidth: isMobile ? playerScore ? '100px' : '140px' : '240px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'bottom',
            }}
          >
            {getPlayerName(player.id)}
          </Box>
          {playerScore && (
            <Box component="span"> {playerScore}</Box>
          )}
        </Typography>
      </Box>
    );
  };

  const getFilteredMyInfo = () => {
    if (!myLeaderboardInfo?.myInfo || !Array.isArray(myLeaderboardInfo.myInfo)) {
        return [];
    }
    
    return myLeaderboardInfo.myInfo.filter(entry => {
        // First check if entry exists
        if (!entry) return false;

          // Only show users with data for this specific interval
          // if the interval deadline has passed
          const intervalNumber = parseInt(interval, 10);
          
          if (hasThisIntervalsDeadlinePassed) {
              return entry?.intervalData?.some(
                  interval => interval.interval === intervalNumber
              ) || false;
          }
          
          // If deadline hasn't passed, show all users
          return true;
      }
    );
  };



  // Filter the leaderboard data based on the selected interval
  // If hasThisGamesFirstIntervalDeadlinePassed is true, and the user does not have that interval's ranking data,
  // then filter them OUT of the leaderboard (so they don't show up at all)
  const getFilteredLeaderboardData = () => {
    if (!leaderboardData || !Array.isArray(leaderboardData)) {
        return [];
    }
    
    return leaderboardData.filter(entry => {
        // First check if entry exists and has required properties
        if (!entry) return false;

          // Only show users with data for this specific interval
          // if the interval deadline has passed
          const intervalNumber = parseInt(interval, 10);
          
          if (hasThisIntervalsDeadlinePassed) {
              return entry?.intervalData?.some(
                  interval => interval.interval === intervalNumber
              ) || false;
          }
          
          // If deadline hasn't passed, show all users
          return true;
        }
    );
  };

  // Replace the existing filteredLeaderboardData implementation with this:
  const filteredLeaderboardData = getFilteredLeaderboardData();

  // Sort the leaderboard by the rank in intervalData where the interval matches
  const sortedLeaderboardData = filteredLeaderboardData?.sort((a, b) => {
    const aRank = a?.intervalData?.find(data => data.interval === interval)?.rank || Infinity;
    const bRank = b?.intervalData?.find(data => data.interval === interval)?.rank || Infinity;
    return aRank - bRank;
  });

  // Get paginated data
  const paginatedData = sortedLeaderboardData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      {/* Top pagination */}
      <Box sx={{ maxWidth: '750px', margin: '0 auto' }}>

        {isUserInThisPool && getFilteredMyInfo()?.length === 0 && (
            <Typography
            sx={{
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 500,
              color: '#CC293C',
              textAlign: 'center',
              width: isMobile ? '90%' : '50%',
              maxWidth: isMobile ? '400px' : '500px',
              margin: '0 auto',
            }}
          >
            {`You did not have any entry in ${intervalName?.startsWith('The') ? intervalName : `The ${intervalName}`}.`}
          </Typography>          
        )}

        <TablePagination
          rowsPerPageOptions={[50, 100]}
          component="div"
          count={leaderboardData?.length || 0}
          rowsPerPage={rowsPerPage}
          page={Math.min(page, Math.max(0, Math.ceil((leaderboardData?.length || 0) / rowsPerPage) - 1))}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows"
          sx={{
            backgroundColor: 'white',
            '& .MuiTablePagination-toolbar': {
              color: '#002129',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input, & .MuiTablePagination-actions': {
              color: '#002129',
            }
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ overflowY: 'auto', maxWidth: '750px', margin: '0 auto', borderRadius: '0px' }}>
        <Table>
          {/* Render user entries if they exist */}
          {getFilteredMyInfo()?.length > 0 && (
            <TableHead>
              {getFilteredMyInfo()?.map((entry, index) => {
                const intervalPlayers = getIntervalPlayersForEntry(entry, interval);
                const worstTiers = getWorstTiersForEntry(intervalPlayers);
                
                return (
                  <TableRow 
                    key={`user-${index}`} 
                    sx={{ backgroundColor: '#F0F1F1' }}
                  >
                    <TableCell align="left" sx={{ verticalAlign: 'top', padding: '8px' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: isMobile ? '12px' : '14px' }}>
                        {entry.username} 
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: isMobile ? '12px' : '14px' }}>
                        Rank: {findRank(entry, interval)} {findScore(entry, interval)}
                      </Typography>
                    </TableCell>
                    <TableCell align="left" sx={{ padding: '8px'}}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {intervalPlayers.map(player => renderPlayerTiers(player, worstTiers))}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableHead>
          )}

          {/* Table header */}
          <TableHead>
            <TableRow sx={{ backgroundColor: '#002129' }}>
              <TableCell align="center" sx={{ fontWeight: 800, color: '#fff' }}>Player</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800, color: '#fff' }}>Roster</TableCell>
            </TableRow>
          </TableHead>
          
          {/* Render non-user entries */}
          <TableBody>
            {paginatedData?.map((entry, index) => {
              const intervalPlayers = getIntervalPlayersForEntry(entry, interval);
              const worstTiers = getWorstTiersForEntry(intervalPlayers);
              
              return (
                <TableRow 
                  key={index} 
                  sx={{ backgroundColor: entry.userId === myUserId ? '#FFF6DA' : index % 2 === 0 ? '#FFF' : '#F0F1F1' }}
                >
                  <TableCell align="left" sx={{ verticalAlign: 'top', padding: '8px' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: isMobile ? '12px' : '14px' }}>
                      {entry.username} 
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: isMobile ? '12px' : '14px' }}>
                      Rank: {findRank(entry, interval)} {findScore(entry, interval)}
                    </Typography>
                  </TableCell>
                  <TableCell align="left" sx={{ padding: '8px'}}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {intervalPlayers.map(player => renderPlayerTiers(player, worstTiers))}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bottom pagination */}
      <Box sx={{ maxWidth: '750px', margin: '0 auto', marginBottom: '20px' }}>
        <TablePagination
          rowsPerPageOptions={[50, 100]}
          component="div"
          count={leaderboardData?.length || 0}
          rowsPerPage={rowsPerPage}
          page={Math.min(page, Math.max(0, Math.ceil((leaderboardData?.length || 0) / rowsPerPage) - 1))}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows"
          sx={{
            backgroundColor: 'white',
            '& .MuiTablePagination-toolbar': {
              color: '#002129',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input, & .MuiTablePagination-actions': {
              color: '#002129',
            }
          }}
        />
      </Box>
    </>
  );
};

export default TiersPlayerPicks;